import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../../components/NavBar/NavBar';
import logo from '../../assets/svgs/logo circular1.png';
import wintoCoin from '../../assets/images/winto-coin.png';
import './ShortInvestmentPage.css';
import Footer from '../../components/Footer/Footer';
import DiscoverReturns from '../../components/DiscoverReturns/DiscoverReturns';
import { useCurrency } from '../../contexts/CurrencyContext';
import { createCheckoutSession, getStripe } from '../../utils/stripeConfig';
import { calculateWinicoins } from '../../utils/winicoinsUtils';
import useAuthListener from '../../hooks/useAuthListener';
import { auth, googleProvider, appleProvider } from '../../firebase/firebase';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { useLanguage } from '../../contexts/LanguageContext';

const ShortInvestmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const heroRef = useRef(null);
  const howItWorksRef = useRef(null);
  const whatYouGetRef = useRef(null);
  const plansRef = useRef(null);
  const { user } = useAuthListener();
  
  // Use shared currency context
  const { 
    selectedCurrency, 
    convertToSelectedCurrency,
    availableCurrencies
  } = useCurrency();
  
  // Use language context
  const { translations: t } = useLanguage();
  
  // Duration popup state
  const [showDurationPopup, setShowDurationPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('6');
  const [isProcessing, setIsProcessing] = useState(false);

  // Sign-in popup state
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);

  // Duration options
  const durationOptions = [
    { value: '6', label: '6 months', multiplier: 4 },
    { value: '12', label: '12 months', multiplier: 8 },
    { value: '24', label: '24 months', multiplier: 12 }
  ];

  // Plan data mapping
  const planData = {
    'basic-6': { amount: 72, name: 'Basic' },
    'starter-12': { amount: 144, name: 'Starter' },
    'growth-30': { amount: 360, name: 'Growth' },
    'advanced-60': { amount: 720, name: 'Advanced' },
    'professional-120': { amount: 1440, name: 'Professional' },
    'enterprise-300': { amount: 3600, name: 'Enterprise' },
    'premium-600': { amount: 7200, name: 'Premium' },
    'ultimate-1200': { amount: 14400, name: 'Ultimate' },
    'elite-3000': { amount: 36000, name: 'Elite' },
    'royal-6000': { amount: 72000, name: 'Royal' },
    'empire-12000': { amount: 144000, name: 'Empire' },
    'legacy-30000': { amount: 360000, name: 'Legacy' }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const refs = [heroRef, howItWorksRef, whatYouGetRef, plansRef];
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Handle scroll to plans section when navigating from success/cancel pages
  useEffect(() => {
    if (location.state?.scrollToPlans && plansRef.current) {
      setTimeout(() => {
        plansRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [location.state]);

  const handleGetStarted = () => {
    if (plansRef.current) {
      plansRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleHighlightCTA = () => {
    navigate('/investment');
  };

  // Handle plan selection - open duration popup
  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setSelectedDuration('6');
    setShowDurationPopup(true);
  };

  const handleDurationSelect = (duration) => {
    setSelectedDuration(duration);
  };

  // Get ISO code for the currently selected currency (3-letter format)
  const getSelectedCurrencyIso = () => {
    const match = Array.isArray(availableCurrencies)
      ? availableCurrencies.find((currency) => currency.name === selectedCurrency)
      : null;
    return (match && match.name) ? match.name : (selectedCurrency || 'USD');
  };

  // Handle invest now from popup
  const handleInvestNow = async () => {
    if (isProcessing || !selectedPlan || isSigningIn) return;

    const currentUser = user || auth.currentUser;
    if (!currentUser) {
      setShowSignInPopup(true);
      return;
    }

    setIsProcessing(true);
    
    try {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const plan = planData[selectedPlan];
      const duration = durationOptions.find(d => d.value === selectedDuration);
      if (!plan || !duration) {
        throw new Error('Invalid plan or duration');
      }

      const originalAmount = parseInt(plan.amount, 10);
      const convertedAmount = typeof convertToSelectedCurrency === 'function'
        ? convertToSelectedCurrency(originalAmount)
        : originalAmount;

      const winicoinsToEarn = calculateWinicoins(originalAmount);

      // ✅ Use the new object signature
      const { sessionId } = await createCheckoutSession({
        amount: convertedAmount,
        planTitle: plan.name,
        duration: selectedDuration,
        multiplier: duration.multiplier,
        currency: getSelectedCurrencyIso(), // e.g., USD/AED
        customerEmail: currentUser.email,
        metadata: {
          userId: currentUser.uid,
          originalAmount,
          winicoinsToEarn,
          purchaseType: 'investment',
          planTitle: plan.name,
        },
      });

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error('Stripe error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowDurationPopup(false);
    }
  };

  // Close popup
  const closeDurationPopup = () => {
    setShowDurationPopup(false);
    setSelectedPlan(null);
    setSelectedDuration('6');
  };

  // Sign-in handlers
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowSignInPopup(false);
      setEmail('');
      setPassword('');
      setIsSigningIn(false);
      setTimeout(() => {
        handleInvestNow();
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, googleProvider);
      setShowSignInPopup(false);
      setIsSigningIn(false);
      setTimeout(() => {
        handleInvestNow();
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const handleAppleLogin = async () => {
    setAuthError('');
    setIsSigningIn(true);
    try {
      await signInWithPopup(auth, appleProvider);
      setShowSignInPopup(false);
      setIsSigningIn(false);
      setTimeout(() => {
        handleInvestNow();
      }, 100);
    } catch (err) {
      setAuthError(err.message);
      setIsSigningIn(false);
    }
  };

  const closeSignInPopup = () => {
    setShowSignInPopup(false);
    setEmail('');
    setPassword('');
    setAuthError('');
    setIsSigningIn(false);
  };

  const formatNumberWithCommas = (value, fractionDigits = 2) => {
    const num = Number(value);
    if (!isFinite(num)) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  };

  const formatConvertedAmount = (amount, fractionDigits = 2) => {
    const n = Number(amount) || 0;
    const converted = typeof convertToSelectedCurrency === 'function' 
      ? convertToSelectedCurrency(n) 
      : n;
    return formatNumberWithCommas(converted, fractionDigits);
  };

  const scrollContainerRef = useRef(null);

  return (
    <>
      <NavBar/>
      <div className="short-term-investment-page">
        {/* Hero Section */}
        <section ref={heroRef} className="short-term-hero-section">
          <div className="short-term-hero-background">
            <div className="short-term-hero-gradient"></div>
            <div className="short-term-dynamic-background">
              <div className="short-term-floating-shape short-term-shape-1"></div>
              <div className="short-term-floating-shape short-term-shape-2"></div>
              <div className="short-term-floating-shape short-term-shape-3"></div>
              <div className="short-term-floating-shape short-term-shape-4"></div>
              <div className="short-term-floating-shape short-term-shape-5"></div>
              <div className="short-term-gradient-orb short-term-orb-1"></div>
              <div className="short-term-gradient-orb short-term-orb-2"></div>
              <div className="short-term-gradient-orb short-term-orb-3"></div>
            </div>
          </div>
          
          <div className="short-term-hero-container">
            <div className="short-term-hero-content">
              <div className="short-term-logo-container short-term-animate-on-scroll">
                <img src={logo} alt="iWinto Logo" className="short-term-logo" />
              </div>
              <h1 className="short-term-hero-title short-term-animate-on-scroll">
                {t.shortInvestment?.hero?.title || "Winicoins, Working Overtime"}
              </h1>
              <p className="short-term-hero-subtitle short-term-animate-on-scroll">
                {t.shortInvestment?.hero?.subtitle || "Start with as little as $72 and see your money grow in months."}<br/> 
                {t.shortInvestment?.hero?.subtitle2 || "and unlock perks across our apps."}
              </p>
              <div className="short-term-hero-stats">
                <div className="short-term-stat short-term-bounce-in">
                  <span className="short-term-stat-number">400 - 1200%</span>
                  <span className="short-term-stat-label">{t.shortInvestment?.hero?.stats?.averageReturns || "Average Returns"}</span>
                </div>
                <div className="short-term-stat short-term-bounce-in">
                  <span className="short-term-stat-number">6-24</span>
                  <span className="short-term-stat-label">{t.shortInvestment?.hero?.stats?.duration || "Months Duration"}</span>
                </div>
                <div className="short-term-stat short-term-bounce-in">
                  <span className="short-term-stat-number">$72</span>
                  <span className="short-term-stat-label">{t.shortInvestment?.hero?.stats?.minimumAmount || "Minimum Amount"}</span>
                </div>
              </div>
              <button className="short-term-hero-cta short-term-animate-on-scroll" onClick={handleGetStarted}>
                {t.shortInvestment?.hero?.startButton || "Start Now"}
              </button>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section ref={howItWorksRef} className="short-term-how-it-works-section">
          <div className="short-term-container">
            <div className="short-term-section-header short-term-animate-on-scroll">
              <div className="short-term-header-decoration">
                <div className="short-term-decoration-line"></div>
                <div className="short-term-decoration-dot"></div>
                <div className="short-term-decoration-line"></div>
              </div>
              <h2 className="short-term-gradient-title">{t.shortInvestment?.howItWorks?.title || "How It Works"}</h2>
              <p className="short-term-subtitle">{t.shortInvestment?.howItWorks?.subtitle || "Your journey to financial growth in four simple steps"}</p>
            </div>
            
            <div className="short-term-timeline-container">
              <div className="short-term-timeline-line"></div>
              
              <div className="short-term-timeline-item short-term-timeline-left" data-step="1">
                <div className="short-term-timeline-marker">
                  <div className="short-term-timeline-icon">
                    <span className="short-term-timeline-number">1</span>
                    <div className="short-term-timeline-glow"></div>
                  </div>
                </div>
                <div className="short-term-timeline-content">
                    <div className="short-term-timeline-header">
                      <h3>{t.shortInvestment?.howItWorks?.step1?.title || "Choose Your Plan"}</h3>
                      <div className="short-term-timeline-indicator"></div>
                    </div>
                    <p>{t.shortInvestment?.howItWorks?.step1?.description || "Select from our curated short-term money multiplication options based on your goals and risk tolerance."}</p>
                    <div className="short-term-timeline-features">
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">✨</span>
                        {t.shortInvestment?.howItWorks?.step1?.feature1 || "Flexible Options"}
                      </span>
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">📊</span>
                        {t.shortInvestment?.howItWorks?.step1?.feature2 || "Risk Assessment"}
                      </span>
                    </div>
                </div>
              </div>
              
              <div className="short-term-timeline-item short-term-timeline-right" data-step="2">
                <div className="short-term-timeline-marker">
                  <div className="short-term-timeline-icon">
                    <span className="short-term-timeline-number">2</span>
                    <div className="short-term-timeline-glow"></div>
                  </div>
                </div>
                <div className="short-term-timeline-content">
                    <div className="short-term-timeline-header">
                      <h3>{t.shortInvestment?.howItWorks?.step2?.title || "Invest Your Amount"}</h3>
                      <div className="short-term-timeline-indicator"></div>
                    </div>
                    <p>{t.shortInvestment?.howItWorks?.step2?.description || "Start with as little as $72. No hidden fees, transparent pricing, and instant confirmation."}</p>
                    <div className="short-term-timeline-features">
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">💰</span>
                        {t.shortInvestment?.howItWorks?.step2?.feature1 || "Min $72"}
                      </span>
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">🔒</span>
                        {t.shortInvestment?.howItWorks?.step2?.feature2 || "No Hidden Fees"}
                      </span>
                    </div>
                </div>
              </div>
              
              <div className="short-term-timeline-item short-term-timeline-left" data-step="3">
                <div className="short-term-timeline-marker">
                  <div className="short-term-timeline-icon">
                    <span className="short-term-timeline-number">3</span>
                    <div className="short-term-timeline-glow"></div>
                  </div>
                </div>
                <div className="short-term-timeline-content">
                    <div className="short-term-timeline-header">
                      <h3>{t.shortInvestment?.howItWorks?.step3?.title || "Watch It Grow"}</h3>
                      <div className="short-term-timeline-indicator"></div>
                    </div>
                    <p>{t.shortInvestment?.howItWorks?.step3?.description || "Track your progress in real-time. Get notifications on milestones and returns."}</p>
                    <div className="short-term-timeline-features">
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">📈</span>
                        {t.shortInvestment?.howItWorks?.step3?.feature1 || "Real-time Tracking"}
                      </span>
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">🔔</span>
                        {t.shortInvestment?.howItWorks?.step3?.feature2 || "Smart Notifications"}
                      </span>
                    </div>
                </div>
              </div>
              
              <div className="short-term-timeline-item short-term-timeline-right" data-step="4">
                <div className="short-term-timeline-marker">
                  <div className="short-term-timeline-icon">
                    <span className="short-term-timeline-number">4</span>
                    <div className="short-term-timeline-glow"></div>
                  </div>
                </div>
                <div className="short-term-timeline-content">
                    <div className="short-term-timeline-header">
                      <h3>{t.shortInvestment?.howItWorks?.step4?.title || "Cash Out & Repeat"}</h3>
                      <div className="short-term-timeline-indicator"></div>
                    </div>
                    <p>{t.shortInvestment?.howItWorks?.step4?.description || "Withdraw your profits or reinvest for compound growth."}</p>
                    <div className="short-term-timeline-features">
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">💳</span>
                        {t.shortInvestment?.howItWorks?.step4?.feature1 || "Instant Withdrawal"}
                      </span>
                      <span className="short-term-timeline-feature">
                        <span className="short-term-feature-icon">🚀</span>
                        {t.shortInvestment?.howItWorks?.step4?.feature2 || "Compound Growth"}
                      </span>
                    </div>
                </div>
              </div>
            </div>
            
            <div className="short-term-process-highlight short-term-animate-on-scroll">
              <div className="short-term-highlight-background">
                <div className="short-term-highlight-particles"></div>
              </div>
              <div className="short-term-highlight-content">
                <div className="short-term-highlight-icon-modern">🤝</div>
                <h3 className="investment-partnership-title">
                  {t.shortInvestment?.partnership?.title || "Want a Partnership That Builds Wealth?"}
                </h3>
                <p>{t.shortInvestment?.partnership?.description || "Join our exclusive partnership program and unlock unlimited earning potential. Build a sustainable business that generates passive income while helping others achieve financial freedom."}</p>
                <button className="short-term-highlight-cta-modern" onClick={handleHighlightCTA}>
                  <span className="short-term-cta-text">{t.shortInvestment?.partnership?.button || "Explore Partnership"}</span>
                  <span className="short-term-cta-arrow">→</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section ref={whatYouGetRef} className="short-term-what-you-get-section">
          <div className="short-term-container">
            <div className="short-term-section-header short-term-animate-on-scroll">
              <h2>{t.shortInvestment?.whatYouGet?.title || "What You Get"}</h2>
              <p>{t.shortInvestment?.whatYouGet?.subtitle || "Exclusive benefits designed for you"}</p>
            </div>
          
            <div className="short-term-benefits-grid">
              <div className="short-term-benefit-card short-term-scale-up">
                <div className="short-term-benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="800px" height="800px" viewBox="0 0 24 24" fill="white">
                    <path d="M12.5497 3.43948C12.118 2.50904 11.9022 2.04382 11.5213 2.00452C11.1405 1.96522 10.8468 2.37786 10.2595 3.20314L10.1075 3.41665C9.9406 3.65117 9.85715 3.76843 9.73698 3.83908C9.61681 3.90973 9.47352 3.92577 9.18695 3.95785L8.92606 3.98706C7.91761 4.09997 7.41339 4.15643 7.26346 4.50779C7.11353 4.85916 7.41719 5.27274 8.02450 6.09990L8.18162 6.31389C8.35420 6.54895 8.44049 6.66647 8.47180 6.80408C8.50310 6.94169 8.47588 7.08381 8.42143 7.36804L8.37186 7.62681C8.18025 8.62703 8.08445 9.12714 8.37265 9.38360C8.66086 9.64006 9.14220 9.48302 10.10490 9.16895L10.35390 9.08770C10.62750 8.99845 10.76430 8.95383 10.90380 8.96823C11.04330 8.98262 11.16980 9.05441 11.42270 9.19799L11.65300 9.32871C12.54300 9.83397 12.98800 10.08660 13.31600 9.89374C13.64410 9.70087 13.63790 9.19024 13.62560 8.16897L13.62240 7.90476C13.61890 7.61455 13.61710 7.46944 13.67200 7.34073C13.72700 7.21202 13.83230 7.11427 14.04310 6.91878L14.23500 6.74080C14.97670 6.05285 15.34750 5.70887 15.26200 5.33322C15.17660 4.95756 14.69140 4.79901 13.72110 4.48190C13.47010 4.39986 13.19430 4.30975 13.05650 4.26469C12.95090 4.17074 12.84530 4.07680 12.78400 3.94460 12.66130 3.68020L12.54970 3.43948Z" fill="white"/>
                    <g opacity="0.5">
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.20540 11.27900C11.60370 11.39280 11.83430 11.80790 11.72050 12.20620C11.35410 13.48880 11.26610 15.34980 11.32400 17.21440C11.38130 19.06010 11.57810 20.82960 11.74070 21.88610C11.80360 22.29550 11.52280 22.67840 11.11340 22.74140C10.70400 22.80440 10.32110 22.52360 10.25810 22.11420C10.08730 21.00390 9.884120 19.17350 9.824750 17.26090C9.765960 15.36710 9.844710 13.31140 10.27820 11.79410C10.39200 11.39580 10.80720 11.16520 11.20540 11.27900Z" fill="white"/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M22.64740 9.122220C22.85610 9.480010 22.73530 9.939250 22.37750 10.14800C16.66570 13.47980 15.24960 18.85900 15.24960 22.00010C15.24960 22.41430 14.91380 22.75010 14.49960 22.75010C14.08530 22.75010 13.74960 22.41430 13.74960 22.00010C13.74960 18.47460 15.33340 12.52040 21.62170 8.852290C21.97940 8.643580 22.43870 8.764430 22.64740 9.122220Z" fill="white"/>
                    </g>
                    <path d="M5.133030 13.76490C5.110310 13.65730 5.281800 13.55830 5.363600 13.63180C5.601350 13.84530 5.922250 14.08850 6.222150 14.16890C6.522050 14.24920 6.921540 14.19910 7.234210 14.13300C7.341780 14.11030 7.440800 14.28180 7.367330 14.36360C7.153790 14.60140 6.910620 14.92230 6.830260 15.22210C6.749900 15.52200 6.800050 15.92150 6.866100 16.23420C6.888820 16.34180 6.717330 16.44080 6.635530 16.36730C6.397780 16.15380 6.076880 15.91060 5.776980 15.83030C5.477080 15.74990 5.077590 15.80000 4.764920 15.86610C4.657350 15.88880 4.558340 15.71730 4.631800 15.63550C4.845340 15.39780 5.088510 15.07690 5.168870 14.77700C5.249230 14.47710 5.199080 14.07760 5.133030 13.76490Z" fill="white"/>
                  </svg>
                </div>
                <h3>{t.shortInvestment?.whatYouGet?.benefit1?.title || "Quick Returns"}</h3>
                <p>{t.shortInvestment?.whatYouGet?.benefit1?.description || "See your first returns in as little as 24 weeks with our optimized strategies."}</p>
              </div>
              
              <div className="short-term-benefit-card short-term-scale-up">
                <div className="short-term-benefit-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="white"/>
                  </svg>
                </div>
                <h3>{t.shortInvestment?.whatYouGet?.benefit2?.title || "Instant Withdrawals"}</h3>
                <p>{t.shortInvestment?.whatYouGet?.benefit2?.description || "Access your profits anytime with instant withdrawal to your bank account."}</p>
              </div>
              
              <div className="short-term-benefit-card short-term-scale-up">
                <div className="short-term-benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="white" height="800px" width="800px" viewBox="0 1.3 256 256">
                    <path d="M127.4,102c15.3,0,27.5-12.2,27.5-27.5S142.7,47,127.4,47S99.9,59.3,99.9,74.5S112.2,102,127.4,102z M12.4,108.2h41.9v41.9  H12.4V108.2z M224.1,108.2l-20.8,41.9h41.9L224.1,108.2z M240.8,157.3h-43.1L173,121.2c-7.2-10.3-15.6-14.7-27.2-14.7h-36.7  c-11.7,0-20,4.7-27.2,14.7l-24.7,36.1H14.4c-17.2,0-16.4,24.4-0.3,24.4h49.4c3.9,0,8.6-1.4,11.4-5.6l18.9-26.9v63.9h67.5v-63.9  l18.9,26.9c2.8,4.2,7.5,5.6,11.4,5.6H241C256.9,181.8,258,157.3,240.8,157.3z"/>
                  </svg>
                </div>
                <h3>{t.shortInvestment?.whatYouGet?.benefit3?.title || "Flexible Options"}</h3>
                <p>{t.shortInvestment?.whatYouGet?.benefit3?.description || "Choose from multiple  durations and amounts that fit your schedule, with the potential to grow your money by 4x to 12x."}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Discover Returns Section */}
        <DiscoverReturns />

        {/* Micro Plans Section */}
        <section ref={plansRef} className="short-term-micro-plans-section">
          <div className="short-term-container">
            <div className="short-term-section-header short-term-animate-on-scroll">
              <h2>{t.shortInvestment?.plans?.title || "Choose Your Money Multiplication Plan"}</h2>
              <p>{t.shortInvestment?.plans?.subtitle?.replace('{{minAmount}}', formatConvertedAmount(72)).replace('{{maxAmount}}', formatConvertedAmount(360000)).replace('{{currency}}', getSelectedCurrencyIso()) || `Flexible options from ${formatConvertedAmount(72)} ${getSelectedCurrencyIso()} to ${formatConvertedAmount(360000)} ${getSelectedCurrencyIso()}. Start small and scale up as you grow`}</p>
            </div>
            
            <div className="short-term-plans-scroll-container short-term-animate-on-scroll" ref={scrollContainerRef}>
              <div className="short-term-plans-scroll">
                {/* Cards omitted for brevity — unchanged layout */}
                {/* ... keep all your plan cards exactly as you had them ... */}
                {/* Each "Start with ..." button calls handlePlanSelect('<plan-id>') */}
                {/* Your existing card markup stays the same */}
                
                {/* Basic */}
                <div className="short-term-plan-card short-term-animate-on-scroll">
                  <div className="short-term-plan-header">
                    <h3>Basic</h3>
                    <div className="short-term-plan-price">
                      <span className="short-term-amount">{formatConvertedAmount(72)} {getSelectedCurrencyIso()}</span>
                      <div className="short-term-duration">
                        <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                        <span>6 WIN</span>
                      </div>
                    </div>
                  </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      <span className="multiplier">(×4) {formatConvertedAmount(288)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      <span className="multiplier">(×8) {formatConvertedAmount(576)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      <span className="multiplier">(×12) {formatConvertedAmount(864)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('basic-6')}>
                    {t.shortInvestment?.plans?.startWith?.replace('{{amount}}', formatConvertedAmount(72)).replace('{{currency}}', getSelectedCurrencyIso()) || `Start with ${formatConvertedAmount(72)} ${getSelectedCurrencyIso()}`}
                  </button>
                </div>
                <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Starter</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(144)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>12 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(576)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(1152)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(1728)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('starter-12')}>
{t.shortInvestment?.plans?.startWith?.replace('{{amount}}', formatConvertedAmount(144)).replace('{{currency}}', getSelectedCurrencyIso()) || `Start with ${formatConvertedAmount(144)} ${getSelectedCurrencyIso()}`}
                  </button>
                </div>
               
                                     <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Growth</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(360)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>30 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(1440)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(2880)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(4320)} after 2 years</span>
                    </div>
                    
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('growth-30')}>
                    Start with {formatConvertedAmount(360)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                                 <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Advanced</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(720)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>60 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(2880)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(5760)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(8640)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('advanced-60')}>
                    Start with {formatConvertedAmount(720)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                   <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Professional</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(1440)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>120 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(5760)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(11520)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(17280)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('professional-120')}>
                    Start with {formatConvertedAmount(1440)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                                     <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Enterprise</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(3600)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>300 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(14400)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(28800)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(43200)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('enterprise-300')}>
                    Start with {formatConvertedAmount(3600)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                                       <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Premium</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(7200)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>600 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(28800)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(57600)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(86400)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('premium-600')}>
                    Start with {formatConvertedAmount(7200)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                                     <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Ultimate</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(14400)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>1,200 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(57600)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(115200)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(172800)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('ultimate-1200')}>
                    Start with {formatConvertedAmount(14400)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                                                               <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Elite</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(36000)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>3,000 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(144000)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(288000)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(432000)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('elite-3000')}>
                    Start with {formatConvertedAmount(36000)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
                                                               <div className="short-term-plan-card short-term-animate-on-scroll">
                  <div className="short-term-plan-header">
                    <h3>Royal</h3>
                    <div className="short-term-plan-price">
                      <span className="short-term-amount">{formatConvertedAmount(72000)} {getSelectedCurrencyIso()}</span>
                      <div className="short-term-duration">
                        <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                        <span>6,000 WIN</span>
                      </div>
                    </div>
                  </div>
                 <div className="short-term-plan-features">
                   <div className="short-term-feature">
                     {/* <span className="short-term-check">✓</span> */}
                     <span className="multiplier">(×4) {formatConvertedAmount(288000)} after 6 months</span>
                   </div>
                   <div className="short-term-feature">
                     {/* <span className="short-term-check">✓</span> */}
                     <span className="multiplier">(×8) {formatConvertedAmount(576000)} after 1 year</span>
                   </div>
                   <div className="short-term-feature">
                     {/* <span className="short-term-check">✓</span> */}
                     <span className="multiplier">(×12) {formatConvertedAmount(864000)} after 2 years</span>
                   </div>
                 </div>
                 <button className="short-term-plan-cta" onClick={() => handlePlanSelect('royal-6000')}>
                   Start with {formatConvertedAmount(72000)} {getSelectedCurrencyIso()}
                 </button>
               </div>
               
                                 <div className="short-term-plan-card short-term-animate-on-scroll">
                   <div className="short-term-plan-header">
                     <h3>Empire</h3>
                     <div className="short-term-plan-price">
                       <span className="short-term-amount">{formatConvertedAmount(144000)} {getSelectedCurrencyIso()}</span>
                       <div className="short-term-duration">
                         <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                         <span>12,000 WIN</span>
                       </div>
                     </div>
                   </div>
                  <div className="short-term-plan-features">
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×4) {formatConvertedAmount(576000)} after 6 months</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×8) {formatConvertedAmount(1152000)} after 1 year</span>
                    </div>
                    <div className="short-term-feature">
                      {/* <span className="short-term-check">✓</span> */}
                      <span className="multiplier">(×12) {formatConvertedAmount(1728000)} after 2 years</span>
                    </div>
                  </div>
                  <button className="short-term-plan-cta" onClick={() => handlePlanSelect('empire-12000')}>
                    Start with {formatConvertedAmount(144000)} {getSelectedCurrencyIso()}
                  </button>
                </div>
               
               <div className="short-term-plan-card short-term-animate-on-scroll">
                 <div className="short-term-plan-header">
                   <h3>Legacy</h3>
                   <div className="short-term-plan-price">
                     <span className="short-term-amount">{formatConvertedAmount(360000)} {getSelectedCurrencyIso()}</span>
                     <div className="short-term-duration">
                       <img src={wintoCoin} alt="Winicoin" className="short-term-coin-icon" />
                       <span>30,000 WIN</span>
                     </div>
                   </div>
                 </div>
                 <div className="short-term-plan-features">
                   <div className="short-term-feature">
                     {/* <span className="short-term-check">✓</span> */}
                     <span className="multiplier">(×4) {formatConvertedAmount(1440000)} after 6 months</span>
                   </div>
                   
                   <div className="short-term-feature">
                     {/* <span className="short-term-check">✓</span> */}
                     <span className="multiplier">(×8) {formatConvertedAmount(2880000)} after 1 year</span>
                   </div>
                   <div className="short-term-feature">
                     {/* <span className="short-term-check">✓</span> */}
                     <span className="multiplier">(×12) {formatConvertedAmount(4320000)} after 2 years</span>
                   </div>
                 </div>
                 <button className="short-term-plan-cta" onClick={() => handlePlanSelect('legacy-30000')}>
                   Start with {formatConvertedAmount(360000)} {getSelectedCurrencyIso()}
                 </button>
               </div>
             {/* </div> */}

                {/* ... keep the rest of the plan cards exactly as in your file ... */}

                {/* For brevity, I’m not repeating every single card here. 
                    Copy/paste over your original cards — the functional change is only in handleInvestNow. */}
              </div>
            </div>

            <div className="exchange-rate-card slide-up-fade">
              <div className="exchange-rate-content">
                <div className="exchange-rate-icon">
                  <img src={wintoCoin} alt="Winicoin" />
                </div>
                
                <h3 className="exchange-rate-title">{t.shortInvestment?.exchangeRate?.title || "Current Exchange Rate"}</h3>
                
                <div className="exchange-rate-display">
                  <div className="currency-pair">
                    <span className="currency-amount">1</span>
                    <span className="currency-code">WIN</span>
                  </div>
                  
                  <div className="equals-sign">=</div>
                  
                  <div className="currency-pair">
                    <span className="currency-amount">12</span>
                    <span className="currency-code">USD</span>
                  </div>
                </div>
                
                <div className="exchange-rate-note">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>{t.shortInvestment?.exchangeRate?.note || "Rates update in real-time"}</span>
                </div>
              </div>
            </div>

          </div>
        </section>
        <Footer/>
      </div>

      {/* Duration Selection Popup */}
      {showDurationPopup && (
        <div className="duration-popup-overlay" onClick={closeDurationPopup}>
          <div className="duration-popup" onClick={(e) => e.stopPropagation()}>
            <div className="duration-popup-header">
              <h2>{t.shortInvestment?.durationPopup?.title || "Select The Duration"}</h2>
              <button className="duration-popup-close" onClick={closeDurationPopup}>
                ×
              </button>
            </div>
            
            <div className="duration-popup-content">
              <div className="duration-options">
                {durationOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`duration-option ${selectedDuration === option.value ? 'selected' : ''}`}
                    onClick={() => handleDurationSelect(option.value)}
                  >
                    <div className="duration-option-header">
                      <h3>{option.label}</h3>
                      <div className="duration-multiplier">×{option.multiplier}</div>
                    </div>
                    <div className="duration-option-details">
                      <p>Return: {option.multiplier * 100}%</p>
                      {selectedPlan && (
                        <p className="duration-return-amount">
                          You'll receive: {formatConvertedAmount(planData[selectedPlan].amount * option.multiplier)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="duration-popup-actions">
                <button 
                  className="duration-invest-button"
                  onClick={handleInvestNow}
                  disabled={isProcessing}
                >
                  {isProcessing ? (t.shortInvestment?.durationPopup?.processing || 'Processing...') : !user ? (t.shortInvestment?.durationPopup?.signInToPurchase || 'Sign in to purchase') : (t.shortInvestment?.durationPopup?.buyNow || 'Buy Now')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign-in Popup */}
      {showSignInPopup && (
        <div className="signin-popup-overlay" onClick={closeSignInPopup}>
          <div className="signin-popup" onClick={(e) => e.stopPropagation()}>
            <div className="signin-popup-header">
              <h2>{t.shortInvestment?.signInPopup?.title || "Sign In to Continue"}</h2>
              <button className="signin-popup-close" onClick={closeSignInPopup}>
                ×
              </button>
            </div>
            
            <div className="signin-popup-content">
              <div className="signin-form-container">
                <div className="signin-form-wrapper">
                  <div className="signin-header">
                    <div className="signin-logo-border-radius">
                      <img src={logo} alt="logo" />
                    </div>
                    <h1>{t.shortInvestment?.signInPopup?.welcome || "Welcome to iWinto"}</h1>
                  </div>

                  <form onSubmit={handleEmailLogin} className="signin-form">
                    <div className="form-group">
                      <input
                        type="email"
                        placeholder={t.shortInvestment?.signInPopup?.emailPlaceholder || "Email"}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="password"
                        placeholder={t.shortInvestment?.signInPopup?.passwordPlaceholder || "Password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <button type="submit" className="signin-button">
                      {t.shortInvestment?.signInPopup?.loginButton || "Log in"}
                    </button>
                    {!!authError && <p className="signin-error">{authError}</p>}
                  </form>

                  <div className="signin-divider"><span>{t.shortInvestment?.signInPopup?.orContinueWith || "Or Continue With"}</span></div>
                  <div className="signin-social-buttons">
                    <button onClick={handleGoogleLogin} className="signin-google-button">
                      <img
                        src="https://www.google.com/favicon.ico"
                        alt="Google"
                        className="signin-google-icon"
                      />
                      Google
                    </button>
                    <button onClick={handleAppleLogin} className="signin-apple-button">
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg"
                        alt="Apple"
                        className="signin-apple-icon"
                      />
                      Apple
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShortInvestmentPage;
